import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { CONTENT_TYPE_LABELS, type StudioContentType } from "@/lib/schema";
import {
  ArrowLeft,
  BookOpen,
  Construction,
  FileText,
  Headphones,
  Map,
  Sparkles,
  Star,
  Video,
} from "lucide-react";
import { Link } from "wouter";

const typeIcons: Record<StudioContentType, React.ElementType> = {
  audio: Headphones,
  video: Video,
  blog: FileText,
  recipe: FileText,
  workbook: BookOpen,
  journey: Map,
  ritual: Sparkles,
  astrology: Star,
  affirmation: Star,
};

const typeDescriptions: Record<StudioContentType, string> = {
  audio:
    "Upload or record audio content like guided meditations and breathwork sessions",
  video: "Create video content with thumbnails and transcripts",
  blog: "Write rich-text articles with images and formatting",
  recipe: "Share wellness recipes with ingredients and preparation steps",
  workbook: "Build interactive workbooks with prompts, checklists, and scales",
  journey: "Compose multi-day programmes with structured daily content",
  ritual: "Design step-by-step rituals with timed media elements",
  astrology: "Create zodiac-aligned content and horoscope entries",
  affirmation: "Craft positive affirmations with categories and tags",
};

export default function StudioCreate() {
  const contentTypes = Object.entries(CONTENT_TYPE_LABELS) as [
    StudioContentType,
    string,
  ][];

  return (
    <div
      className="p-6 space-y-6 max-w-4xl mx-auto"
      data-testid="studio-create"
    >
      <div className="flex items-center gap-4 flex-wrap">
        <Link href="/studio">
          <Button
            variant="ghost"
            size="icon"
            data-testid="button-back-overview"
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
        </Link>
        <div>
          <h1
            className="font-serif text-2xl font-semibold text-foreground"
            data-testid="text-create-title"
          >
            Create New Content
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Choose a content type to get started
          </p>
        </div>
      </div>

      <Card className="border-dashed" data-testid="card-phase1-notice">
        <CardContent className="flex items-start gap-4 p-6">
          <div className="p-3 rounded-md bg-muted shrink-0">
            <Construction className="h-6 w-6 text-muted-foreground" />
          </div>
          <div className="space-y-2">
            <h2 className="text-lg font-medium text-foreground">
              Content system rebuilding — Phase 1
            </h2>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Content creation is temporarily unavailable while the system is
              being restructured. Taxonomy management (categories, tags, albums)
              remains available from the overview.
            </p>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {contentTypes.map(([type, label]) => {
          const Icon = typeIcons[type];
          return (
            <Card
              key={type}
              className="opacity-50 h-full"
              data-testid={`card-create-${type}`}
            >
              <CardHeader className="flex flex-row items-center gap-3 pb-2">
                <div className="p-2 rounded-md bg-muted">
                  <Icon className="h-5 w-5 text-muted-foreground" />
                </div>
                <CardTitle className="text-base text-muted-foreground">
                  {label}
                </CardTitle>
                <Badge
                  variant="secondary"
                  className="ml-auto text-xs no-default-hover-elevate no-default-active-elevate"
                >
                  Phase 2
                </Badge>
              </CardHeader>
              <CardContent>
                <p className="text-xs text-muted-foreground">
                  {typeDescriptions[type]}
                </p>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
